import React, { useState } from 'react';
import { ButtonBase, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { ChevronsDown, ChevronsUp, TextAlignStart } from 'lucide-react';
import { Typo } from 'components/primitive';
import { convertToTitleCase } from 'utils/index.ts';
import useStyles from './DrawerMenu.styles.ts';
import JPDASubMenu, { JPDASubMenuProps } from './JPDASubMenu.tsx';

export type SettingsRow = {
  key: string;
  title: string;
  subtitle: string;
  icon: React.ReactElement;
  /** A row the user may not change still shows its value, but choosing it does nothing. */
  enabled: boolean;
  onClick: () => void;
};

type SideMenuSettingsProps = {
  rows: SettingsRow[];
  /** JPDA Start / End, for state users only. */
  jpda?: JPDASubMenuProps;
  onExpand: () => void;
};

/**
 * The side menu's footer: a "Settings" toggle, collapsed by default, that shows the user's data
 * viewing incident, data gathering incident, county and applicant, plus JPDA for state users. The
 * menu unmounts its footer when it closes, so every visit starts collapsed.
 */
const SideMenuSettings: React.FC<SideMenuSettingsProps> = ({ rows, jpda, onExpand }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const [expanded, setExpanded] = useState(false);

  const toggle = () => {
    if (!expanded) onExpand();
    setExpanded((prevState) => !prevState);
  };

  const ToggleIcon = expanded ? ChevronsDown : ChevronsUp;

  return (
    <Box sx={classes.settingsContainer}>
      <ButtonBase
        focusRipple
        sx={classes.helperContainer}
        aria-expanded={expanded}
        onClick={toggle}>
        <Typo fontSize={'12px'} sx={{ color: '#000' }}>
          {expanded ? 'Close Settings' : 'Settings'}
        </Typo>
        <ToggleIcon color='#000' size={18} />
      </ButtonBase>
      {expanded ? (
        <>
          {jpda ? (
            <>
              <Box sx={classes.jpdaTitle}>
                <TextAlignStart color={theme.palette.primary.dark} size={18} />
                <Typo fontSize={'14px'}>JPDA</Typo>
              </Box>
              <JPDASubMenu {...jpda} />
            </>
          ) : null}
          <Box sx={classes.sectionSubGroupContainer}>
            {rows.map((row) => (
              <ButtonBase
                key={row.key}
                focusRipple
                sx={classes.sectionSubGroupBtn.container}
                disabled={!row.enabled}
                onClick={row.onClick}>
                {row.icon}
                <Box sx={classes.sectionSubGroupBtn.txtContainer}>
                  <Typo sx={classes.sectionSubGroupBtn.txtTitle}>
                    {convertToTitleCase(row.title)}
                  </Typo>
                  <Typo sx={classes.sectionSubGroupBtn.txtSubTitle}>
                    {convertToTitleCase(row.subtitle)}
                  </Typo>
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  );
};

export default SideMenuSettings;
